/**
 * Ask (ARCHITECTURE.md §12, Phase 6 groundwork): a question in plain words, answered from the
 * system's fixed queries and nothing else. The answer arrives with the queries it rests on —
 * each one's SQL, or the code path it took, and the rows the model was shown — so "show me the
 * query" is a click, and every figure in the answer is one of those rows' own. A question the
 * data cannot answer says so; an answer that stated a figure the rows do not hold is not shown
 * at all, only why.
 *
 * The API queues the question for the worker and this page polls the run it makes; there is no
 * model call from the browser and no state of the page's own — every question is a run in the
 * Control Center's history.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type AskJson, type AskListResponse, type AskResponse } from '../api.js';

const POLL_MS = 1500;
/** After this long with no run, the worker is probably not running (or has no model key). */
const PATIENCE_MS = 90_000;

const EXAMPLES = [
  'How much cash do we have?',
  'Where will cash be in 90 days?',
  'Which properties have the lowest margins?',
  'Which transactions need review?',
  'Show me our debt schedule.',
  'What is our policy on hot tub chemicals?',
];

function isDone(status: AskJson['status'] | undefined): boolean {
  return status === 'COMPLETED' || status === 'FAILED';
}

export function AskPage(): ReactNode {
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [askedAt, setAskedAt] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: (q: string) =>
      api.post<{ request_id: string; status: string }>('/api/ask', { question: q }),
    onSuccess: (data) => {
      setError(null);
      setRequestId(data.request_id);
      setAskedAt(Date.now());
    },
    onError: (err: Error) => setError(err.message),
  });

  const current = useQuery({
    queryKey: ['ask', requestId],
    enabled: requestId !== null,
    queryFn: () => api.get<AskResponse>(`/api/ask/${requestId}`),
    refetchInterval: (query) => (isDone(query.state.data?.ask.status) ? false : POLL_MS),
  });

  const history = useQuery({
    queryKey: ['ask', 'history'],
    queryFn: () => api.get<AskListResponse>('/api/ask'),
  });

  // A finished run belongs in the history list too.
  const status = current.data?.ask.status;
  useEffect(() => {
    if (isDone(status)) void queryClient.invalidateQueries({ queryKey: ['ask', 'history'] });
  }, [status, queryClient]);

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const q = question.trim();
    if (q === '') return;
    ask.mutate(q);
  };

  const waitedTooLong =
    requestId !== null && status === 'QUEUED' && Date.now() - askedAt > PATIENCE_MS;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Ask</h1>
          <p className="subtitle">
            A question in plain words, answered from the system&apos;s own queries — every figure
            copied from a row you can see here, never worked out by the model. Money questions run
            the finance queries; how-we-do-things questions read{' '}
            <Link to="/knowledge">policies, episodes and documents</Link>.
          </p>
        </div>
      </header>

      <section className="card">
        <form onSubmit={onSubmit} className="filters">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="How much cash do we have?"
            maxLength={500}
            style={{ minWidth: '28rem' }}
          />
          <button type="submit" disabled={question.trim() === '' || ask.isPending}>
            Ask
          </button>
        </form>
        <p className="muted small">
          Try:{' '}
          {EXAMPLES.map((e, i) => (
            <span key={e}>
              {i > 0 ? ' · ' : ''}
              <button type="button" className="link" onClick={() => setQuestion(e)}>
                {e}
              </button>
            </span>
          ))}
        </p>
        {error && <p className="error">{error}</p>}
      </section>

      {requestId !== null && (
        <section className="card">
          {current.isError && <p className="error">{current.error.message}</p>}
          {(status === 'QUEUED' || status === 'RUNNING' || current.isLoading) && !waitedTooLong && (
            <p className="muted">
              {status === 'RUNNING' ? 'Running the queries and writing the answer…' : 'Queued…'}
            </p>
          )}
          {waitedTooLong && (
            <p className="error">
              No answer has arrived. The worker may not be running, or it may have no model key —
              its log says which. The question is still queued and will run when it can.
            </p>
          )}
          {current.data && isDone(status) && <AskResult ask={current.data.ask} />}
        </section>
      )}

      <section className="card">
        <h2>Recent questions</h2>
        {history.data && history.data.asks.length === 0 && (
          <p className="muted">Nothing asked yet.</p>
        )}
        {history.data && history.data.asks.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Asked</th>
                  <th>Question</th>
                  <th>Status</th>
                  <th>Confidence</th>
                  <th>Queries</th>
                </tr>
              </thead>
              <tbody>
                {history.data.asks.map((a) => (
                  <tr key={a.run_id ?? a.request_id ?? ''}>
                    <td className="small">
                      {a.asked_at ? new Date(a.asked_at).toLocaleString() : ''}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          if (a.request_id) {
                            setRequestId(a.request_id);
                            setAskedAt(Date.now());
                          }
                        }}
                      >
                        {a.question}
                      </button>
                    </td>
                    <td>
                      <span
                        className={`chip ${a.status === 'COMPLETED' ? 'chip-ok' : 'chip-attention'}`}
                      >
                        {a.status}
                      </span>
                    </td>
                    <td>{a.confidence ?? '—'}</td>
                    <td className="small">
                      {(a.queries ?? []).map((q) => q.key).join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function AskResult({ ask }: { ask: AskJson }): ReactNode {
  if (ask.status !== 'COMPLETED') {
    return (
      <>
        <h2>No answer</h2>
        <p className="error">{ask.error ?? 'The run did not complete.'}</p>
        {ask.run_id && (
          <p className="muted small">
            Run {ask.run_id.slice(0, 8)} — its timeline is on the{' '}
            <Link to="/agents">Control Center</Link>.
          </p>
        )}
      </>
    );
  }
  return (
    <>
      <h2>{ask.question}</h2>
      <p style={{ whiteSpace: 'pre-wrap' }}>{ask.answer}</p>
      <p className="muted small">
        Confidence {ask.confidence} · {ask.model ?? 'model unknown'} · run {ask.run_id?.slice(0, 8)}{' '}
        · sources:{' '}
        {(ask.citations ?? []).map((c) => `${c.query} (${c.used_for})`).join('; ') || 'none'}
      </p>
      {ask.note && <p className="muted small">Planner&apos;s note: {ask.note}</p>}
      {(ask.queries ?? []).length === 0 && (
        <p className="muted">No query fit the question, so nothing was read.</p>
      )}
      {(ask.queries ?? []).map((q) => (
        <details key={`${q.key}-${JSON.stringify(q.params)}`} className="query-detail">
          <summary>
            <strong>{q.key}</strong>{' '}
            <span className="muted small">
              {Object.keys(q.params).length > 0 ? JSON.stringify(q.params) : ''} · {q.rows.length}{' '}
              row{q.rows.length === 1 ? '' : 's'}
              {q.truncated ? ' (first rows only)' : ''}
            </span>
          </summary>
          <pre className="small" style={{ whiteSpace: 'pre-wrap' }}>
            {q.source}
          </pre>
          {q.rows.length > 0 && (
            <div className="table-wrap">
              <table className="table small">
                <thead>
                  <tr>
                    {Object.keys(q.rows[0]!).map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {q.rows.map((row, i) => (
                    <tr key={i}>
                      {Object.keys(q.rows[0]!).map((col) => (
                        <td key={col}>{cell(row[col])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      ))}
    </>
  );
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
